import { useOutletContext } from "react-router";

import type { OrgRecord } from "@/shared/domain/orgs";

/**
 * Outlet-context payload that the `/devices/:deviceId` parent layout
 * (`<Device />`) hands to each child tab (datasets, files, sessions,
 * details, tokens) through `<Outlet context>`.
 *
 * Fields:
 * 1. `deviceId` — resolved from the URL path param; non-empty by the time
 *    a tab renders, since the parent layout short-circuits to an error
 *    state when it is missing.
 * 2. `org` — the viewer's active organization, or `null` while auth is
 *    still resolving.
 *
 * The device record itself is intentionally absent. Only the details tab
 * needs the full record, and it fetches via `useDevice` (TanStack Query
 * caches the response), so siblings that just need the id avoid a
 * redundant request.
 */
export type DeviceContext = {
  deviceId: string;
  org: OrgRecord | null;
};

/**
 * Read `{ deviceId, org }` from the device-detail outlet context.
 *
 * Call from any descendant route of `<Device />` (i.e. any tab under
 * `/devices/:deviceId`) to get the same bundle the parent layout
 * already resolved, rather than re-reading `useParams` / `useAuth`
 * separately in every tab.
 *
 * Throws if called outside a `<Device />` outlet — react-router's
 * `useOutletContext` returns `null` in that case, which the hook surfaces
 * as a named error so the misuse points at this hook rather than at a
 * downstream destructure.
 */
export function useDeviceContext(): DeviceContext {
  const context = useOutletContext<DeviceContext | null>();
  if (context === null) {
    throw new Error(
      "useDeviceContext must be called inside a <Device /> outlet",
    );
  }
  return context;
}
